import React, { useState, useEffect } from 'react';
import { fabricantesAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Edit, ChevronRight, Loader2, Trash2 } from 'lucide-react';
import { formatApiError } from '../../utils/apiError';

const AdminFabricantes = () => {
  const [fabricantes, setFabricantes] = useState([]);
  const [rubros, setRubros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFab, setSelectedFab] = useState(null);
  const [modelos, setModelos] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showModeloForm, setShowModeloForm] = useState(false);
  const [formData, setFormData] = useState({ nombre: '', rubros: [], logo_url: '' });
  const [nuevoModelo, setNuevoModelo] = useState('');
  const [modeloRubro, setModeloRubro] = useState('');
  const [nuevoRubro, setNuevoRubro] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [fabRes, rubrosRes] = await Promise.all([
        fabricantesAPI.getAll(),
        fabricantesAPI.getRubros()
      ]);
      setFabricantes(fabRes.data);
      setRubros(rubrosRes.data);
    } catch (error) {
      toast.error('Error al cargar fabricantes');
    } finally {
      setLoading(false);
    }
  };

  const loadModelos = async (fabricanteId) => {
    try {
      const response = await fabricantesAPI.getModelos(fabricanteId);
      setModelos(response.data);
    } catch (error) {
      toast.error('Error al cargar modelos');
    }
  };

  const handleSelectFab = async (fab) => {
    setSelectedFab(fab);
    await loadModelos(fab.id);
  };

  const handleCreateFab = async () => {
    if (!formData.nombre) {
      toast.error('El nombre es requerido');
      return;
    }
    setSaving(true);
    try {
      await fabricantesAPI.create(formData);
      toast.success('Fabricante creado');
      setShowForm(false);
      setFormData({ nombre: '', rubros: [], logo_url: '' });
      loadData();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al crear fabricante'));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateModelo = async () => {
    if (!nuevoModelo || !selectedFab) {
      toast.error('El nombre del modelo es requerido');
      return;
    }
    setSaving(true);
    try {
      await fabricantesAPI.createModelo(selectedFab.id, { 
        fabricante_id: selectedFab.id,
        nombre: nuevoModelo,
        rubro: modeloRubro || selectedFab.rubros?.[0] || ''
      });
      toast.success('Modelo creado');
      setNuevoModelo('');
      setModeloRubro('');
      setShowModeloForm(false);
      loadModelos(selectedFab.id);
    } catch (error) {
      toast.error(formatApiError(error, 'Error al crear modelo'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFabricante = async (fab) => {
    if (!confirm(`¿Está seguro de eliminar el fabricante "${fab.nombre}"?\n\nEsto también eliminará todos los modelos asociados.`)) {
      return;
    }
    try {
      await fabricantesAPI.delete(fab.id);
      toast.success('Fabricante eliminado');
      if (selectedFab?.id === fab.id) {
        setSelectedFab(null);
        setModelos([]);
      }
      loadData();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al eliminar fabricante'));
    }
  };

  const handleDeleteModelo = async (modelo) => {
    if (!confirm(`¿Está seguro de eliminar el modelo "${modelo.nombre}"?`)) {
      return;
    }
    try {
      await fabricantesAPI.deleteModelo(modelo.id);
      toast.success('Modelo eliminado');
      loadModelos(selectedFab.id);
    } catch (error) {
      toast.error(formatApiError(error, 'Error al eliminar modelo'));
    }
  };

  const toggleRubro = (rubro) => {
    setFormData(prev => ({
      ...prev,
      rubros: prev.rubros.includes(rubro)
        ? prev.rubros.filter(r => r !== rubro)
        : [...prev.rubros, rubro]
    }));
  };

  const filteredFabs = fabricantes.filter(f =>
    f.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto py-6 px-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Fabricantes y Modelos</h1>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="mr-2 h-4 w-4" /> Nuevo Fabricante
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Lista de Fabricantes */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg">Fabricantes</CardTitle>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center py-4">Cargando...</p>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {filteredFabs.map(fab => (
                    <div
                      key={fab.id}
                      className={`p-3 rounded-lg cursor-pointer flex items-center justify-between ${
                        selectedFab?.id === fab.id ? 'bg-sky-100 border-sky-500' : 'bg-gray-50 hover:bg-gray-100'
                      } border group`}
                      onClick={() => handleSelectFab(fab)}
                    >
                      <div className="flex items-center gap-3">
                        {fab.logo_url ? (
                          <img 
                            src={fab.logo_url} 
                            alt={fab.nombre} 
                            className="h-10 w-10 object-contain rounded bg-white p-1"
                          />
                        ) : (
                          <div className="h-10 w-10 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">
                            Logo
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{fab.nombre}</p>
                          <div className="flex gap-1 mt-1">
                            {fab.rubros?.slice(0, 2).map(r => (
                              <Badge key={r} variant="outline" className="text-xs">{r}</Badge>
                            ))}
                            {fab.rubros?.length > 2 && (
                              <Badge variant="outline" className="text-xs">+{fab.rubros.length - 2}</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 h-8 w-8 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFabricante(fab);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Modelos del Fabricante Seleccionado */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg">
                  {selectedFab ? `Modelos de ${selectedFab.nombre}` : 'Selecciona un fabricante'}
                </CardTitle>
                {selectedFab && (
                  <Button size="sm" onClick={() => setShowModeloForm(true)}>
                    <Plus className="mr-2 h-4 w-4" /> Nuevo Modelo
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!selectedFab ? (
                <p className="text-center text-gray-500 py-8">
                  Selecciona un fabricante para ver sus modelos
                </p>
              ) : modelos.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  Este fabricante no tiene modelos registrados
                </p>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {modelos.map(modelo => (
                    <div key={modelo.id} className="p-3 bg-gray-50 rounded-lg border flex justify-between items-center group">
                      <p className="font-medium">{modelo.nombre}</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 h-6 w-6 p-0"
                        onClick={() => handleDeleteModelo(modelo)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Dialog: Nuevo Fabricante */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuevo Fabricante</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Ej: Cessna, Lycoming, Hartzell"
                />
              </div>
              <div>
                <Label>URL del Logo (opcional)</Label>
                <Input
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  placeholder="https://ejemplo.com/logo.png"
                />
                {formData.logo_url && (
                  <div className="mt-2 p-2 bg-gray-50 rounded">
                    <img 
                      src={formData.logo_url} 
                      alt="Preview" 
                      className="h-12 object-contain"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
              <div>
                <Label>Rubros</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {rubros.map(rubro => (
                    <Badge
                      key={rubro}
                      variant={formData.rubros.includes(rubro) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleRubro(rubro)}
                    >
                      {rubro}
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2 mt-3">
                  <Input
                    value={nuevoRubro}
                    onChange={(e) => setNuevoRubro(e.target.value)}
                    placeholder="Agregar nuevo rubro..."
                    className="flex-1"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      if (nuevoRubro.trim() && !rubros.includes(nuevoRubro.trim())) {
                        const nuevo = nuevoRubro.trim();
                        setRubros([...rubros, nuevo]);
                        setFormData({ ...formData, rubros: [...formData.rubros, nuevo] });
                        setNuevoRubro('');
                        toast.success(`Rubro "${nuevo}" agregado`);
                      }
                    }}
                    disabled={!nuevoRubro.trim()}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Button onClick={handleCreateFab} disabled={saving} className="w-full">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Crear Fabricante
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Nuevo Modelo */}
        <Dialog open={showModeloForm} onOpenChange={setShowModeloForm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuevo Modelo para {selectedFab?.nombre}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre del Modelo *</Label>
                <Input
                  value={nuevoModelo}
                  onChange={(e) => setNuevoModelo(e.target.value)}
                  placeholder="Ej: 172 Skyhawk, O-360-A1A"
                />
              </div>
              
              <div>
                <Label>Rubro</Label>
                <Select value={modeloRubro} onValueChange={setModeloRubro}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar rubro..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(selectedFab?.rubros || rubros).map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button onClick={handleCreateModelo} disabled={saving} className="w-full">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Crear Modelo
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminFabricantes;
